import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import { type NewsArticleBodyProps, newsArticlePath } from '@/features/actualidad-hostelera/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const ARTICLE_ID = 'ia-hosteleria-ine-2026'
const k = (f: string) => `news.articles.${ARTICLE_ID}.${f}`

const REF = {
    ineTic: {
        href: 'https://www.ine.es/dyngs/INEbase/es/operacion.htm?c=estadistica_C&cid=1254736176743&menu=ultiDatos&idp=1254735576799',
        domain: 'ine.es',
        label: 'INE · TIC empresas',
    },
    europaPress: {
        href: 'https://www.pressdigital.es/articulo/economia/2026-10-22/6061860-336-empresas-espana-diez-empleados-ia-primer-trimestre-ano-segun-ine',
        domain: 'pressdigital.es',
        label: 'Europa Press · Press Digital',
    },
    cehat: {
        href: 'https://cehat.com/wp-content/uploads/2025/02/04022025-La-hosteleria-considera-la-reduccion-de-la-jornada-laboral-una-intromision-en-dialogo-social.pdf',
        domain: 'cehat.com',
        label: 'Hostelería de España · CEHAT',
    },
    ineMicro: {
        href: 'https://www.ine.es/dyngs/Prensa/ETICCE20241T2025.htm',
        domain: 'ine.es',
        label: 'INE · microempresas 2025',
    },
    hip: {
        href: 'https://www.expohip.com/app/uploads/sites/2/2026/03/260312_Un-tercio-de-los-hosteleros-espanoles-preve-abrir-nuevos-locales-este-2026-a-pesar-de-la-inflacion.pdf',
        domain: 'expohip.com',
        label: 'Barómetro HIP 2026',
    },
    ontsi: {
        href: 'https://www.ontsi.es/sites/ontsi/files/2026-07/indicadores-de-uso-de-inteligencia-articifial_1.pdf',
        domain: 'ontsi.es',
        label: 'ONTSI · uso de IA',
    },
    alimarket: {
        href: 'https://www.alimarket.es/restauracion/informe_amp/388650/informe-2024-de-hardware-y-software-para-restauracion',
        domain: 'alimarket.es',
        label: 'Alimarket · software 2024',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

export function IaHosteleriaIneArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'el-336', label: t(k('toc.pct'), 'El 33,6 % que vas a leer en todas partes') },
        { id: 'cuenta-bares', label: t(k('toc.bares'), '¿El INE cuenta a los bares y restaurantes?') },
        { id: 'frena', label: t(k('toc.frena'), 'Lo que frena no es la falta de herramientas') },
        { id: 'tres-preguntas', label: t(k('toc.preguntas'), 'Cómo saber si una herramienta de IA te sirve') },
        { id: 'cuesta-probar', label: t(k('toc.cuesta'), 'Lo que cuesta probar algo que no sirve') },
        { id: 'tipi-test', label: t(k('toc.tipi'), 'Tipi también es IA. Pásanos el test') },
        { id: 'limites', label: t(k('toc.limites'), 'Lo que estos datos todavía no cuentan') },
    ]

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo de la IA en hostelería y el INE y quiero pasaros el test a Tipi (o que miréis una herramienta que me están vendiendo).',
        ),
    )

    const questions: ReactNode[] = [
        <>
            <strong>{t(k('questions.1.strong'), '¿Qué me deja hacer que antes yo no podía?')}</strong>{' '}
            {t(
                k('questions.1.after'),
                'No "qué hace". Si la respuesta es "lo mismo que tal programa, pero con IA", es carcasa. Si es "algo que antes exigía una persona o una tarde con un Excel, y ahora lo haces tú en el hueco del café", hay algo.',
            )}
        </>,
        <>
            <strong>{t(k('questions.2.strong'), '¿Cuánto me pide antes de devolverme algo útil?')}</strong>{' '}
            {t(
                k('questions.2.after'),
                'Si necesitas integrar el TPV, cargar el catálogo entero y una tarde de formación antes del primer resultado, no está pensada para tu local.',
            )}
        </>,
        <>
            <strong>{t(k('questions.3.strong'), '¿Qué pasa cuando se equivoca?')}</strong>{' '}
            {t(
                k('questions.3.after'),
                'Porque se va a equivocar. Pregunta cómo te enteras, quién lo corrige y cuánto tarda. Una herramienta que no sabe contestar a eso no está terminada, la hayan hecho en un mes o en cinco años.',
            )}
        </>,
    ]

    const limits: ReactNode[] = [
        <>
            <strong>{t(k('limits.1.strong'), 'El dato de microempresas de 2026.')}</strong>{' '}
            {t(
                k('limits.1.after'),
                'Lo publicado el 22 de octubre recoge las de 10 o más empleados. Cuando tengamos la cifra de las de menos de 10, la añadimos aquí.',
            )}
        </>,
        <>
            <strong>{t(k('limits.2.strong'), 'Si bares y restaurantes entran siquiera en el total.')}</strong>{' '}
            {t(
                k('limits.2.after'),
                'Sabemos que las tablas por actividad excluyen la CNAE 56. No hemos podido confirmar si queda fuera también del 33,6 % general. El INE estrena este año la clasificación CNAE-2025 y eso podría cambiar.',
            )}
        </>,
        <>
            <strong>{t(k('limits.3.strong'), 'Si la IA le ha servido a alguien.')}</strong>{' '}
            {t(
                k('limits.3.after'),
                'El INE mide quién la usa, no si le ha ahorrado un euro o una hora. Esa encuesta, hoy, no existe.',
            )}
        </>,
    ]

    const sources = [
        {
            label: t(
                k('sources.1'),
                'INE. Encuesta sobre el uso de TIC y del comercio electrónico en las empresas, 2025 y primer trimestre de 2026. Publicada el 22 de octubre de 2026.',
            ),
            href: REF.ineTic.href,
        },
        {
            label: t(
                k('sources.2'),
                'INE. Nota de prensa de la misma encuesta, 2024 y primer trimestre de 2025. Publicada el 22 de octubre de 2025.',
            ),
            href: REF.ineMicro.href,
        },
        {
            label: t(
                k('sources.3'),
                'Europa Press, vía Press Digital. "El 33,6% de empresas en España de diez o más empleados usó la IA en el primer trimestre del año, según el INE". 22 de octubre de 2026.',
            ),
            href: REF.europaPress.href,
        },
        {
            label: t(
                k('sources.4'),
                'Hostelería de España y CEHAT. Comunicado sobre la reducción de jornada, 4 de febrero de 2025.',
            ),
            href: REF.cehat.href,
        },
        {
            label: t(
                k('sources.5'),
                'HIP Horeca Professional Expo. Barómetro sobre la Competitividad de la Hostelería Española 2026. 12 de marzo de 2026.',
            ),
            href: REF.hip.href,
        },
        {
            label: t(
                k('sources.6'),
                'ONTSI (Red.es). Indicadores de uso de inteligencia artificial en España, edición 2026, datos 2025. Julio de 2026.',
            ),
            href: REF.ontsi.href,
        },
        {
            label: t(k('sources.7'), 'Alimarket. Informes de hardware y software para restauración, 2024 y 2026.'),
            href: REF.alimarket.href,
        },
    ]

    const ownSource = t(
        k('sources.8'),
        'Cuenta de 49 €/mes, horas de puesta en marcha y ejemplo de albaranes: elaboración propia de Tipi para enseñar el mecanismo; no son datos de ninguna herramienta real.',
    )

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(k('dek.beforePct'), 'El INE publicó el 22 de octubre que el ')}
                            <strong>{t(k('dek.pct'), '33,6 %')}</strong>
                            {t(
                                k('dek.afterPct'),
                                ' de las empresas españolas de 10 o más empleados usó inteligencia artificial en el primer trimestre de 2026, 12,5 puntos más que hace un año. Pero el ',
                            )}
                            <strong>{t(k('dek.micro'), '94 %')}</strong>
                            {t(
                                k('dek.afterMicro'),
                                ' de las empresas de hostelería tiene menos de 10 trabajadores, y en las microempresas el último dato era del ',
                            )}
                            <strong>{t(k('dek.microPct'), '13,4 %')}</strong>
                            {t(
                                k('dek.afterMicroPct'),
                                '. Las tablas del INE por actividad ni siquiera desglosan bares y restaurantes. Y entre quienes se plantearon usar IA y no lo hicieron, lo que más frena no es el precio: es no saber.',
                            )}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Barra de bar con un móvil, un albarán y un folleto de herramienta encima del mostrador',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <ArticleReadingLayout
                    intro={
                        <div className={article.prose}>
                            <p className={cn(article.p, 'mt-0')}>
                                {t(
                                    k('open.p1'),
                                    'Esta semana te ha llamado un comercial, o dos. Todos traen una aplicación "con inteligencia artificial" que te va a ahorrar horas, leer tus ventas y contestar las reseñas por ti.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('open.p2'),
                                    'Le preguntas qué hace exactamente y te describe algo que ya existía hace cinco años. Con otro logo.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(k('open.p3.before'), 'No te lo estás imaginando. Antes de firmar nada, pregúntale ')}
                                <strong>{t(k('open.p3.strong'), 'qué te deja hacer que antes no podías')}</strong>
                                {t(k('open.p3.after'), ': si la respuesta es "lo mismo, pero con IA", es carcasa.')}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <section id='el-336'>
                        <h2 className={cn(article.h2, 'mt-0')}>
                            {t(k('h2.pct'), 'El 33,6 % que vas a leer en todas partes')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(
                                        k('pct.p1.before'),
                                        'El Instituto Nacional de Estadística publicó el jueves 22 de octubre su ',
                                    )}
                                    <a
                                        href={REF.ineTic.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(
                                            k('pct.p1.link'),
                                            'Encuesta sobre el uso de TIC y del comercio electrónico en las empresas',
                                        )}
                                    </a>
                                    <SourceChip source='ineTic' />
                                    {'.'}
                                </span>
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('pct.p2.before'),
                                        'El 33,6 % de las empresas de 10 o más empleados usó inteligencia artificial en el primer trimestre de 2026',
                                    )}
                                </strong>
                                <span className={article.cite}>
                                    {t(k('pct.p2.mid'), ', ')}
                                    <a
                                        href={REF.europaPress.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('pct.p2.link'), 'según recoge Europa Press')}
                                    </a>
                                    <SourceChip source='europaPress' />
                                    {t(k('pct.p2.after'), '. Un año antes era el ')}
                                </span>
                                <strong>{t(k('pct.p2.prev'), '21,1 %')}</strong>
                                {'.'}
                            </p>
                            <p className={article.p}>
                                {t(k('pct.p3.before'), 'En el sector servicios la cifra sube al ')}
                                <strong>{t(k('pct.p3.services'), '38,2 %')}</strong>
                                {'.'}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('pct.p4'),
                                    'Leído así, parece que la IA ya está en uno de cada tres negocios. En el tuyo, probablemente no, y no porque vayas tarde.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='cuenta-bares'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.bares'), '¿El INE cuenta a los bares y restaurantes?')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('bares.p1.before'), 'Primera trampa del titular: ')}
                                <strong>
                                    {t(k('bares.p1.strong'), 'el 33,6 % solo mide a empresas de 10 o más empleados.')}
                                </strong>
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    <strong>
                                        {t(
                                            k('bares.p2.strong'),
                                            'El 94 % de las empresas de hostelería tiene menos de 10 trabajadores',
                                        )}
                                    </strong>
                                    {t(k('bares.p2.mid'), ', según ')}
                                    <a
                                        href={REF.cehat.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('bares.p2.link'), 'Hostelería de España y CEHAT')}
                                    </a>
                                    <SourceChip source='cehat' />
                                    {'.'}
                                </span>
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('bares.p3'),
                                    'Dicho de otra forma: de cada 100 empresas del sector, ese titular habla de 6.',
                                )}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(
                                        k('bares.p4.before'),
                                        'Para las microempresas, el último dato publicado por el INE es del ',
                                    )}
                                    <strong>{t(k('bares.p4.pct'), '13,4 % en el primer trimestre de 2025')}</strong>
                                    {t(k('bares.p4.mid'), ', con ')}
                                    <a
                                        href={REF.ineMicro.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('bares.p4.link'), 'todos los sectores mezclados')}
                                    </a>
                                    <SourceChip source='ineMicro' />
                                    {'.'}
                                </span>
                            </p>
                            <p className={article.p}>
                                {t(k('bares.p5.before'), 'La segunda trampa es más gorda. ')}
                                <strong>
                                    {t(
                                        k('bares.p5.strong'),
                                        'Las tablas del INE por actividad económica excluyen la CNAE 56, la de servicios de comidas y bebidas.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('bares.p5.after'),
                                    'No hay una cifra oficial publicada de cuántos bares y restaurantes usan IA.',
                                )}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(
                                        k('bares.p6.before'),
                                        'Todo lo que leas sobre "la hostelería ya usa IA" sale de encuestas privadas. La más citada este año, el ',
                                    )}
                                    <a
                                        href={REF.hip.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('bares.p6.link'), 'Barómetro de HIP')}
                                    </a>
                                    <SourceChip source='hip' />
                                    {t(k('bares.p6.mid'), ', dice que el ')}
                                    <strong>{t(k('bares.p6.pct'), '44,8 %')}</strong>
                                    {t(
                                        k('bares.p6.after'),
                                        ' de quienes planean invertir en tecnología apuesta por IA para analizar datos.',
                                    )}
                                </span>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(k('bares.p7.before'), 'Está hecho con ')}
                                <strong>{t(k('bares.p7.strong'), '268 respuestas de asistentes a HIP')}</strong>
                                {t(
                                    k('bares.p7.after'),
                                    ', la feria de soluciones para hostelería. No es el sector: es la parte del sector que ya fue a mirar.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='frena'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.frena'), 'Lo que frena no es la falta de herramientas')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('frena.p1'),
                                    'Si la IA no llega a los locales pequeños, la respuesta de quien vende es fácil: hacen falta más herramientas, y más baratas. Los datos dicen otra cosa.',
                                )}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    <strong>
                                        {t(
                                            k('frena.p2.strong'),
                                            'Entre las empresas de 10 o más empleados que se plantearon usar IA y no lo hicieron, al 82,4 % le frenó la falta de conocimientos y al 47,4 % el coste',
                                        )}
                                    </strong>
                                    {t(k('frena.p2.mid'), ', según los ')}
                                    <a
                                        href={REF.ontsi.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('frena.p2.link'), 'indicadores de uso de IA del ONTSI')}
                                    </a>
                                    <SourceChip source='ontsi' />
                                    {t(k('frena.p2.after'), ' (Red.es), con datos de 2025.')}
                                </span>
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('frena.p3'),
                                    'Eso en empresas con plantilla. En un local de cuatro personas donde el dueño también está en la plancha, la falta de tiempo para aprender pesa todavía más.',
                                )}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(k('frena.p4.before'), 'La oferta, mientras, no para. El ')}
                                    <a
                                        href={REF.alimarket.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(
                                            k('frena.p4.link'),
                                            'informe de software para restauración de Alimarket de 2024',
                                        )}
                                    </a>
                                    <SourceChip source='alimarket' />
                                    {t(
                                        k('frena.p4.after'),
                                        ' ya describía un mercado saturado de herramientas, muchas sin integración y complejas de usar, y el de 2026 sigue hablando de saturación.',
                                    )}
                                </span>
                            </p>
                            <p className={article.p}>
                                {t(k('frena.p5.before'), 'Ahí está el desajuste. ')}
                                <strong>
                                    {t(k('frena.p5.strong'), 'Programar una aplicación nunca ha sido tan rápido.')}
                                </strong>{' '}
                                {t(
                                    k('frena.p5.after'),
                                    'Construir algo que encaje en la semana de un hostelero sin tardes libres sigue costando lo mismo que siempre: conocer el oficio.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('frena.p6'),
                                    'Lo que vemos desde Tipi es mucha herramienta nueva que hace lo de siempre, con IA en el folleto. Y pocas que usen la IA para lo que de verdad cambia algo: que un local pequeño pueda hacer lo que antes solo hacía un grupo con departamento de control.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='tres-preguntas'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(
                                k('h2.preguntas'),
                                '¿Cómo sé si una herramienta de inteligencia artificial para hostelería me sirve?',
                            )}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('preguntas.p1'), 'Tres preguntas. Hazlas antes de firmar nada.')}
                            </p>
                            <NewsArticleNumList items={questions} />
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(k('preguntas.p2.before'), 'Y una cuarta, de toda la vida: ')}
                                <strong>{t(k('preguntas.p2.strong'), '¿hay permanencia?')}</strong>{' '}
                                {t(k('preguntas.p2.after'), 'Si confían en que te sirve, no necesitan atarte.')}
                            </p>
                        </div>
                    </section>

                    <section id='cuesta-probar'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.cuesta'), 'Lo que cuesta probar algo que no sirve')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('cuesta.p1.before'), 'Pongamos una herramienta de ')}
                                <strong>{t(k('cuesta.p1.price'), '49 € al mes')}</strong>
                                {t(k('cuesta.p1.mid'), ' con permanencia de un año. Son ')}
                                <strong>{t(k('cuesta.p1.year'), '588 €')}</strong>
                                {t(k('cuesta.p1.after'), ' aunque la dejes en marzo.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('cuesta.p2.before'),
                                    'Súmale la puesta en marcha: 8 horas entre configurarla y aprenderla, a 20 € la hora, son otros ',
                                )}
                                <strong>{t(k('cuesta.p2.setup'), '160 €')}</strong>
                                {t(k('cuesta.p2.mid'), '. Unos ')}
                                <strong>{t(k('cuesta.p2.total'), '750 €')}</strong>
                                {t(
                                    k('cuesta.p2.after'),
                                    ' el primer año por algo que, si no pasa la primera pregunta, no te resuelve nada nuevo.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('cuesta.p3.before'),
                                    'Ahora la otra cara: un local con 60 albaranes al mes que tarda 4 minutos en picar cada uno dedica ',
                                )}
                                <strong>{t(k('cuesta.p3.hours'), '4 horas al mes, 48 al año')}</strong>
                                {t(k('cuesta.p3.after'), ', a teclear. Quitarte eso sí cambia tu semana.')}
                            </p>
                            <p className={cn(article.p, 'text-article-muted')}>
                                <em>
                                    {t(
                                        k('cuesta.note'),
                                        'Cuenta hecha por nosotros para enseñar el mecanismo: precio, horas y valor de la hora son supuestos, no datos de ninguna herramienta real.',
                                    )}
                                </em>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('cuesta.p4'),
                                    'La diferencia entre las dos no es la IA. Es si resuelve un trabajo que hoy haces tú.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='tipi-test'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.tipi'), 'Tipi también es IA. Pásanos el test')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('tipi.p1'),
                                    'Hay que decirlo: Tipi usa inteligencia artificial. Escandallas hablando y leemos tus albaranes con OCR.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('tipi.p2'),
                                    'Y el escandallo no lo hemos inventado nosotros: hay herramientas que llevan años haciéndolo bien.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('tipi.p3.before'),
                                    'Si llevas un grupo con cocina central y necesitas conectarlo todo con tu TPV y tu contabilidad, hay ',
                                )}
                                <LocaleLink
                                    to={newsArticlePath('mejor-herramienta-hosteleria-independiente-2026')}
                                    className={article.link}
                                >
                                    {t(k('tipi.p3.link'), 'opciones más completas que la nuestra')}
                                </LocaleLink>
                                {'.'}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('tipi.p4.before'),
                                    'Lo que intentamos es otra cosa: que escandallar lo pueda hacer quien no tiene una tarde libre para sentarse con un Excel, que es justo el motivo por el que ',
                                )}
                                <LocaleLink
                                    to={newsArticlePath('por-que-hosteleria-tarda-digitalizarse-control-costes')}
                                    className={article.link}
                                >
                                    {t(k('tipi.p4.link'), 'tanta hostelería sigue sin controlar costes')}
                                </LocaleLink>
                                {t(
                                    k('tipi.p4.after'),
                                    '. Si eso no es verdad en tu caso, no te servimos, y es mejor que lo sepas antes de pagar.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='limites'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.limites'), 'Lo que estos datos todavía no cuentan')}
                        </h2>
                        <div className={article.prose}>
                            <NewsArticleNumList items={limits} />
                            <p className={article.p}>
                                {t(
                                    k('limites.p1'),
                                    'Si te están vendiendo una herramienta con IA y no tienes claro si pasa las tres preguntas, cuéntanosla y la miramos contigo. Aunque la conclusión sea que la nuestra tampoco te hace falta.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('limites.p2'),
                                    'Y si nos pasas el test a nosotros y no lo aprobamos, también queremos saberlo.',
                                )}
                            </p>
                        </div>
                    </section>
                </ArticleReadingLayout>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿La que te venden pasa las tres preguntas?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Cuéntanosla y la miramos contigo. O pásanos el test a Tipi: escandallos por voz y albaranes con OCR, sin permanencia.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                        <li>{ownSource}</li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
